import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
/* eslint-disable react-refresh/only-export-components */

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Slider,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { formatFixed } from "../../i18n/numberFormat";
import {
  formatSpeedFromKnots,
  kmhToKnots,
  knotsToKmh,
  speedUnitLabel,
} from "../../i18n/unitFormat";
import { useChartSettings } from "../chartSettings/ChartSettingsContext";

type VesselSettings = {
  vesselDraft: number; // meters
  vesselHeight: number; // meters, 0 means no filtering
  vesselSpeed: number; // knots
  setVesselDraft: (v: number) => void;
  setVesselHeight: (v: number) => void;
  setVesselSpeed: (v: number) => void;
  openDialog: () => void;
  closeDialog: () => void;
};

const VesselSettingsContext = createContext<VesselSettings | null>(null);

const DRAFT_DEFAULT = 1.5;
const SPEED_DEFAULT = 6.0;
const SPEED_MIN_KN = 1;
const SPEED_MAX_KN = 30;
const SPEED_STEP_KN = 0.5;

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function readNumber(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

export function VesselSettingsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const locale = i18n.language;
  const { useSiUnits } = useChartSettings();

  const [vesselDraft, _setVesselDraft] = useState<number>(() =>
    readNumber("vesselDraft", DRAFT_DEFAULT),
  );
  const [vesselHeight, _setVesselHeight] = useState<number>(() =>
    readNumber("vesselHeight", 0),
  );
  const [vesselSpeed, _setVesselSpeed] = useState<number>(() =>
    readNumber("vesselSpeed", SPEED_DEFAULT),
  );

  const [dialogOpen, setDialogOpen] = useState(false);

  const setVesselDraft = useCallback((v: number) => {
    _setVesselDraft(clamp(Number(v), 0, 5));
  }, []);

  const setVesselHeight = useCallback((v: number) => {
    _setVesselHeight(clamp(Number(v), 0, 40));
  }, []);

  const setVesselSpeed = useCallback((v: number) => {
    _setVesselSpeed(clamp(Number(v), SPEED_MIN_KN, SPEED_MAX_KN));
  }, []);

  const speedDisplay = useSiUnits ? knotsToKmh(vesselSpeed) : vesselSpeed;
  const speedMin = useSiUnits ? knotsToKmh(SPEED_MIN_KN) : SPEED_MIN_KN;
  const speedMax = useSiUnits ? knotsToKmh(SPEED_MAX_KN) : SPEED_MAX_KN;
  const speedStep = useSiUnits ? knotsToKmh(SPEED_STEP_KN) : SPEED_STEP_KN;
  const speedUnit = speedUnitLabel(useSiUnits);

  const setSpeedFromDisplay = useCallback(
    (displayValue: number) => {
      const knots = useSiUnits ? kmhToKnots(displayValue) : displayValue;
      setVesselSpeed(knots);
    },
    [setVesselSpeed, useSiUnits],
  );

  useEffect(() => {
    try {
      localStorage.setItem("vesselDraft", String(vesselDraft));
    } catch {
      // Ignore storage errors
    }
  }, [vesselDraft]);

  useEffect(() => {
    try {
      localStorage.setItem("vesselHeight", String(vesselHeight));
    } catch {
      // Ignore storage errors
    }
  }, [vesselHeight]);

  useEffect(() => {
    try {
      localStorage.setItem("vesselSpeed", String(vesselSpeed));
    } catch {
      // Ignore storage errors
    }
  }, [vesselSpeed]);

  const openDialog = useCallback(() => setDialogOpen(true), []);
  const closeDialog = useCallback(() => setDialogOpen(false), []);

  const dialogPaperSx = useMemo(
    () => ({ minWidth: { xs: "100%", sm: 420 } }),
    [],
  );

  const formatDraftLabel = useCallback(
    (value: number) => formatFixed(value, locale, 1),
    [locale],
  );
  const formatHeightLabel = useCallback(
    (value: number) => formatFixed(value, locale, 1),
    [locale],
  );
  const formatSpeedLabel = useCallback(
    (value: number) => formatFixed(value, locale, 1),
    [locale],
  );

  const applyPresetSail = useCallback(() => {
    setVesselDraft(1.8);
    setVesselHeight(15);
    setVesselSpeed(6);
  }, [setVesselDraft, setVesselHeight, setVesselSpeed]);
  const applyPresetMotor = useCallback(() => {
    setVesselDraft(1.0);
    setVesselHeight(3);
    setVesselSpeed(18);
  }, [setVesselDraft, setVesselHeight, setVesselSpeed]);

  const value = useMemo<VesselSettings>(
    () => ({
      vesselDraft,
      vesselHeight,
      vesselSpeed,
      setVesselDraft,
      setVesselHeight,
      setVesselSpeed,
      openDialog,
      closeDialog,
    }),
    [
      vesselDraft,
      vesselHeight,
      vesselSpeed,
      setVesselDraft,
      setVesselHeight,
      setVesselSpeed,
      openDialog,
      closeDialog,
    ],
  );

  return (
    <VesselSettingsContext.Provider value={value}>
      {children}

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullScreen={fullScreen}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle>{t("vesselSettings.title")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="subtitle1">
                {t("vesselSettings.draft")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("vesselSettings.draftHint")}
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <Box sx={{ flex: 1, width: "100%" }}>
                  <Slider
                    value={vesselDraft}
                    onChange={(_, v) => setVesselDraft(v as number)}
                    step={0.1}
                    min={0}
                    max={5}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) =>
                      formatDraftLabel(value as number)
                    }
                    aria-label="Vessel draft"
                  />
                </Box>

                <TextField
                  value={vesselDraft}
                  onChange={(e) => setVesselDraft(Number(e.target.value))}
                  type="number"
                  inputProps={{ step: 0.1, min: 0, max: 5 }}
                  size="small"
                  sx={{ width: 100, minWidth: 100 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">m</InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Typography variant="h5" align="center" sx={{ mt: 1 }}>
                {formatFixed(vesselDraft, locale, 1)} m
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1">
                {t("vesselSettings.height")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("vesselSettings.heightHint")}
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <Box sx={{ flex: 1, width: "100%" }}>
                  <Slider
                    value={vesselHeight}
                    onChange={(_, v) => setVesselHeight(v as number)}
                    step={0.5}
                    min={0}
                    max={40}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) =>
                      formatHeightLabel(value as number)
                    }
                    aria-label="Vessel height"
                  />
                </Box>

                <TextField
                  value={vesselHeight}
                  onChange={(e) => setVesselHeight(Number(e.target.value))}
                  type="number"
                  inputProps={{ step: 0.5, min: 0, max: 40 }}
                  size="small"
                  sx={{ width: 100, minWidth: 100 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">m</InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Typography variant="h5" align="center" sx={{ mt: 1 }}>
                {formatFixed(vesselHeight, locale, 1)} m
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1">
                {t("vesselSettings.speed")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("vesselSettings.speedHint")}
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <Box sx={{ flex: 1, width: "100%" }}>
                  <Slider
                    value={speedDisplay}
                    onChange={(_, v) => setSpeedFromDisplay(v as number)}
                    step={speedStep}
                    min={speedMin}
                    max={speedMax}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) =>
                      formatSpeedLabel(value as number)
                    }
                    aria-label="Vessel average speed"
                  />
                </Box>

                <TextField
                  value={Number(speedDisplay.toFixed(1))}
                  onChange={(e) => setSpeedFromDisplay(Number(e.target.value))}
                  type="number"
                  inputProps={{
                    step: speedStep,
                    min: speedMin,
                    max: speedMax,
                  }}
                  size="small"
                  sx={{ width: 110, minWidth: 110 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {speedUnit}
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Typography variant="h5" align="center" sx={{ mt: 1 }}>
                {formatSpeedFromKnots(vesselSpeed, locale, useSiUnits)}
              </Typography>
            </Box>

            <Divider />

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={t("vesselSettings.presetSail")}
                onClick={applyPresetSail}
              />
              <Chip
                label={t("vesselSettings.presetMotor")}
                onClick={applyPresetMotor}
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDialog}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>
    </VesselSettingsContext.Provider>
  );
}

export function useVesselSettings() {
  const v = useContext(VesselSettingsContext);
  if (!v)
    throw new Error(
      "useVesselSettings must be used within VesselSettingsProvider",
    );
  return v;
}
